import React from 'react';
import './VoicemailRetrySettings.css';

/** Upper bound for the “within last N days” window (align with backend / product). */
export const DUPLICATE_DATA_RULE_MAX_DAYS = 365;

/** Prefill / placeholder when turning the rule on or field is empty (must enter 1–365 to save). */
export const DUPLICATE_DATA_RULE_DEFAULT_DAYS = 0;

/**
 * Optional per-campaign rule: reject new contacts when the same phone was added recently.
 * Layout matches Voicemail Retry outer shell (grey box + inner panel); copy per Rules mock.
 */
export default function DuplicateDataRuleSection({
    enabled,
    days,
    onEnabledChange,
    onDaysChange,
    disabled = false,
    daysError,
    idPrefix = 'dup-rule',
    footer = null,
    chromeless = false,
    hideDescription = false,
}) {
    const daysInputDisabled = disabled || !enabled;

    const toggleBlock = (
        <div className="form-group voicemail-retry-toggle">
            <div className="voicemail-retry-toggle-wrapper">
                <label className="voicemail-retry-toggle-label mb-0">
                    Enable data rule
                </label>

                <span
                    className={
                        enabled
                            ? 'kt-switch kt-switch-sm kt-switch--success chat-cus-check voicemail-retry-switch'
                            : 'kt-switch kt-switch-sm kt-switch--error chat-cus-check voicemail-retry-switch'
                    }
                >
                    <label>
                        <input
                            type="checkbox"
                            id={`${idPrefix}-enabled`}
                            checked={!!enabled}
                            onChange={(e) => {
                                const checked = e.target.checked;
                                onEnabledChange(checked);
                                if (
                                    checked &&
                                    (days === '' || days === null || days === undefined)
                                ) {
                                    onDaysChange(DUPLICATE_DATA_RULE_DEFAULT_DAYS);
                                }
                            }}
                            disabled={disabled}
                        />
                        <span />
                    </label>
                </span>
            </div>
        </div>
    );

    const fieldsContent = (
        <>
            <div className="voicemail-retry-field-label mb-2">Rejection window</div>
            <div className="form-group mb-0 duplicate-data-rule-days-field">
                <div
                    className="d-flex flex-wrap align-items-center duplicate-data-rule-days-row"
                    style={{ gap: 8, rowGap: 8 }}
                >
                    <span className="voicemail-retry-send-now-label voicemail-retry-toggle-label--multiline">
                        Reject if number was added within the last
                    </span>
                    <input
                        id={`${idPrefix}-days`}
                        type="number"
                        min={0}
                        max={DUPLICATE_DATA_RULE_MAX_DAYS}
                        step={1}
                        className={`form-control ${daysError ? 'is-invalid' : ''}`}
                        value={
                            days === '' || days === null || days === undefined
                                ? ''
                                : days
                        }
                        placeholder={String(DUPLICATE_DATA_RULE_DEFAULT_DAYS)}
                        title={
                            daysInputDisabled
                                ? 'Turn on the rule to edit the number of days'
                                : `Whole days, 1–${DUPLICATE_DATA_RULE_MAX_DAYS}`
                        }
                        onChange={(e) => {
                            const v = e.target.value;
                            if (v === '') {
                                onDaysChange('');
                                return;
                            }
                            const n = parseInt(v, 10);
                            if (Number.isNaN(n)) return;
                            onDaysChange(
                                Math.min(
                                    DUPLICATE_DATA_RULE_MAX_DAYS,
                                    Math.max(0, n)
                                )
                            );
                        }}
                        disabled={daysInputDisabled}
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            width: 76,
                            minWidth: 76,
                            maxWidth: 96,
                            boxSizing: 'border-box',
                        }}
                    />
                    <span className="voicemail-retry-send-now-label">days</span>
                </div>
                <div className="duplicate-data-rule-range-hint">
                    Applies to manual entry, CSV upload, and webhook or triggered leads. Range: 1 –{' '}
                    {DUPLICATE_DATA_RULE_MAX_DAYS} days.
                </div>
            </div>
            {daysError && (
                <div
                    className="invalid-feedback d-block mt-1"
                    style={{ color: '#dc3545', fontSize: '12px' }}
                    role="alert"
                >
                    {daysError}
                </div>
            )}
        </>
    );

    if (chromeless && hideDescription) {
        return (
            <div
                className={`voicemail-retry-settings duplicate-data-rule-voicemail-shell voicemail-retry-settings--chromeless${
                    enabled ? ' voicemail-retry-settings--expanded' : ''
                }${disabled ? ' duplicate-data-rule-voicemail-shell--disabled' : ''}`}
            >
                <div className="voicemail-retry-settings__toggle-row">
                    {toggleBlock}
                </div>
                {enabled && (
                    <div className="voicemail-retry-settings__fields-panel">
                        <div className="voicemail-retry-settings-container">
                            {fieldsContent}
                        </div>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div
            className={`voicemail-retry-settings duplicate-data-rule-voicemail-shell${
                chromeless ? ' voicemail-retry-settings--chromeless' : ''
            }${disabled ? ' duplicate-data-rule-voicemail-shell--disabled' : ''}`}
        >
            <div className="row">
                <div className="col-md-12">
                    {toggleBlock}

                    {!hideDescription && (
                        <p className="rules-match-voicemail-desc">
                            Reject numbers that were recently added to prevent re-queuing the same contact.
                        </p>
                    )}

                    {enabled && (
                        <div className="voicemail-retry-settings-container mt-3">
                            {fieldsContent}
                        </div>
                    )}

                    {footer != null && footer !== false && (
                        <div
                            className="duplicate-data-rule-voicemail-shell__footer mt-3 pt-3"
                            style={{ borderTop: '1px solid #e9ecef' }}
                        >
                            {footer}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
